import { useState } from "react";
import {
  Form,
  useDataProvider,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { IncentiveProgram } from "../types";
import { IncentiveProgramInputs } from "./IncentiveProgramInputs";
import { dateOnlyPlusDays, validateNewValidFrom } from "./incentiveProgramForm";

/**
 * Almikia Epic E7 (TASK-095): "Nueva vigencia" -- direccion drafts the NEXT
 * version of `program` from a later `valid_from`, keeping the SAME code
 * (past periods keep their original values, ticket description). This is a
 * two-step write, never a single `create`: the CURRENT row's `valid_to` is
 * closed the day before the new `valid_from` (otherwise its open-ended
 * window would always overlap the new one and the database would refuse
 * it), then the new row is inserted open-ended. Not a `<CreateBase>` --
 * that only issues one `create` call -- so this uses a plain standalone
 * `<Form>` (ra-core) with its own submit handler instead.
 */
export const NewVigenciaDialog = ({
  program,
}: {
  program: IncentiveProgram;
}) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleSubmit = async (values: Record<string, any>) => {
    const newValidFrom = values.valid_from as string;
    setIsSubmitting(true);
    try {
      // Only close the CURRENT row's window when it is still open-ended or
      // already reaches into the new period -- a program that already ended
      // earlier (e.g. `valid_to: "2026-06-30"` with a new version starting
      // later) must keep its own past `valid_to` untouched, never get
      // extended to fill the gap before the new version.
      if (!program.valid_to || program.valid_to >= newValidFrom) {
        await dataProvider.update("incentive_programs", {
          id: program.id,
          data: { valid_to: dateOnlyPlusDays(newValidFrom, -1) },
          previousData: program,
        });
      }
      await dataProvider.create("incentive_programs", {
        data: {
          code: program.code,
          name: program.name,
          scope: program.scope,
          period: program.period,
          direct_sales_only: program.direct_sales_only,
          parameters: values.parameters,
          valid_from: newValidFrom,
          valid_to: null,
        },
      });
      notify("resources.incentive_programs.new_vigencia.success", {
        type: "success",
      });
      setOpen(false);
      refresh();
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "resources.incentive_programs.new_vigencia.error",
        { type: "error" },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        {translate("resources.incentive_programs.action.new_vigencia")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg overflow-y-auto max-h-9/10">
          <Form
            record={{
              valid_from: dateOnlyPlusDays(
                program.valid_to ?? program.valid_from,
                1,
              ),
              parameters: program.parameters,
            }}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate("resources.incentive_programs.new_vigencia.title", {
                  name: program.name,
                })}
              </DialogTitle>
            </DialogHeader>
            <IncentiveProgramInputs
              code={program.code}
              showValidTo={false}
              validateValidFrom={(value) =>
                validateNewValidFrom(program, value as string | null)
              }
            />
            <DialogFooter className="w-full justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                {translate("ra.action.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {translate("resources.incentive_programs.action.new_vigencia")}
              </Button>
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
